var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { borderRadius, borderRadiusValues, borderWidths, borderWidthValues, boxShadows, breakpoints, breakpointValues, colors, dark, fontFamily, fontSizes, fontSizeValues, fontWeights, fontWeightValues, light, lineHeights, lineHeightValues, sizings, sizingValues, spacings, spacingValues, textAligns, zIndexes, } from '@hawkins/variables';
export var HawkinsGlobalClass = {
    ACTIVE: 'Hawkins-active',
    FOCUS_RING: 'Hawkins-focusRing',
    FOCUSABLE: 'Hawkins-focusable',
    HIDDEN: 'Hawkins-hidden',
    HOVER: 'Hawkins-hover',
    ICON: 'Hawkins-Icon',
    ICON_SMALL: 'Hawkins-Icon-Small',
    SELECTED: 'Hawkins-selected',
    TRUNCATE: 'Hawkins-truncate',
};
/**
 * Util function that generates either a min-width or max-width media query
 * by first attempting to see if the breakpoint arg is a valid Hawkins breakpoint,
 * and falling back to using the breakpoint's face value if it doesn't exist.
 */
var createWidthMediaQuery = function (_a, direction, breakpoint) {
    var breakpoints = _a.breakpoints;
    var width = breakpoint;
    if (breakpoint in breakpoints) {
        width = breakpoints[breakpoint];
    }
    else if (typeof breakpoint === 'number') {
        width = "".concat(breakpoint, "px");
    }
    return "@media (".concat(direction === 'down' ? 'max' : 'min', "-width: ").concat(width, ")");
};
export var hawkinsUtils = function (hawkins, hawkinsVariables) {
    return {
        breakpoints: {
            down: function (breakpoint) { return createWidthMediaQuery(hawkinsVariables, 'down', breakpoint); },
            up: function (breakpoint) { return createWidthMediaQuery(hawkinsVariables, 'up', breakpoint); },
        },
        // Generates a selector for active elements,
        // taking into account the relevant CSS pseudo-classes and Hawkins CSS classes.
        getActiveElementSelector: function (prefix, suffix) {
            if (prefix === void 0) { prefix = ''; }
            if (suffix === void 0) { suffix = ''; }
            return [':active', ".".concat(HawkinsGlobalClass.ACTIVE)].map(function (str) { return "".concat(prefix).concat(str).concat(suffix); }).join(', ');
        },
        getCSSVariableFallbackChain: function (varNames, fallbackValue) {
            var hasFallbackValue = !!fallbackValue || fallbackValue === 0;
            if (!varNames.length)
                return String(hasFallbackValue ? fallbackValue : '');
            var varNamesReversed = __spreadArray([], __read(varNames), false).reverse();
            var result = String(hasFallbackValue ? fallbackValue : "var(".concat(varNamesReversed[0], ")"));
            for (var i = hasFallbackValue ? 0 : 1; i < varNamesReversed.length; i++) {
                result = "var(".concat(varNamesReversed[i], ", ").concat(result, ")");
            }
            return result;
        },
        // Generates a selector for disabled elements, taking into account the relevant HTML and ARIA attributes.
        getDisabledElementSelector: function (prefix, suffix) {
            if (prefix === void 0) { prefix = ''; }
            if (suffix === void 0) { suffix = ''; }
            return ['[disabled]', '[aria-disabled="true"]'].map(function (str) { return "".concat(prefix).concat(str).concat(suffix); }).join(', ');
        },
        // Generates a selector for non-disabled elements, taking into account the relevant HTML and ARIA attributes.
        getEnabledElementSelector: function (prefix, suffix) {
            if (prefix === void 0) { prefix = ''; }
            if (suffix === void 0) { suffix = ''; }
            return "".concat(prefix, ":not([disabled]):not([aria-disabled=\"true\"])").concat(suffix);
        },
        // The `multiplier` param is useful for leaving a focus ring-width gap between an element and its focus ring.
        // The `addend` param is useful for accounting for arbitrary widths (e.g., a 1px border) when placing the focus ring.
        getFocusRingPosition: function (multiplier, addend) {
            if (multiplier === void 0) { multiplier = 0; }
            if (addend === void 0) { addend = 0; }
            var focusRingOffset = parseInt(hawkins.focus.borderWidthRegular, 10) * multiplier * -1 - addend;
            return {
                bottom: focusRingOffset,
                left: focusRingOffset,
                right: focusRingOffset,
                top: focusRingOffset,
            };
        },
        // Generates a selector for elements that should be visibly focused,
        // taking into account the relevant CSS pseudo-classes and Hawkins CSS classes.
        getFocusRingedElementSelector: function (prefix, suffix) {
            if (prefix === void 0) { prefix = ''; }
            if (suffix === void 0) { suffix = ''; }
            return [':focus-visible', ".".concat(HawkinsGlobalClass.FOCUS_RING)].map(function (str) { return "".concat(prefix).concat(str).concat(suffix); }).join(', ');
        },
        // Generates the CSS-in-JS styling for an element displaying a focus ring.
        getFocusRingedElementStyle: function (anchorStyleOverride, focusRingStyleOverride) {
            if (anchorStyleOverride === void 0) { anchorStyleOverride = {}; }
            if (focusRingStyleOverride === void 0) { focusRingStyleOverride = {}; }
            return (__assign(__assign({ 
                // Support Windows High Contrast Mode
                // (see https://moderncss.dev/the-3-css-methods-for-adding-element-borders/#ensuring-visible-focus).
                outline: "".concat(hawkins.focus.borderWidthRegular, " solid transparent"), position: 'relative' }, anchorStyleOverride), { '&::after': __assign({ borderColor: hawkins.focus.borderColorRegular, borderRadius: hawkinsVariables.borderRadius.borderRadius0, borderStyle: 'solid', borderWidth: hawkins.focus.borderWidthRegular, bottom: 0, content: '""', left: 0, pointerEvents: 'none', position: 'absolute', right: 0, top: 0, zIndex: hawkinsVariables.zIndexes.appBar }, focusRingStyleOverride) }));
        },
        // Generates a selector for hovered elements,
        // taking into account the relevant CSS pseudo-classes and Hawkins CSS classes.
        getHoveredElementSelector: function (prefix, suffix) {
            if (prefix === void 0) { prefix = ''; }
            if (suffix === void 0) { suffix = ''; }
            return [':hover', ".".concat(HawkinsGlobalClass.HOVER)].map(function (str) { return "".concat(prefix).concat(str).concat(suffix); }).join(', ');
        },
        // Generates a selector for menu items, taking into account the relevant ARIA roles.
        getMenuItemSelector: function (prefix, suffix) {
            if (prefix === void 0) { prefix = ''; }
            if (suffix === void 0) { suffix = ''; }
            return ['[role="menuitem"]', '[role="menuitemcheckbox"]', '[role="menuitemradio"]']
                .map(function (str) { return "".concat(prefix).concat(str).concat(suffix); })
                .join(', ');
        },
    };
};
export var createHawkinsThemeObject = function (theme, externalTheme) {
    if (theme === void 0) { theme = 'light'; }
    var hawkinsTheme = theme === 'light' ? light.theme : dark.theme;
    var hawkinsVariables = {
        borderRadius: borderRadius,
        borderRadiusValues: borderRadiusValues,
        borderWidths: borderWidths,
        borderWidthValues: borderWidthValues,
        boxShadows: boxShadows,
        breakpoints: breakpoints,
        breakpointValues: breakpointValues,
        colors: colors,
        fontFamily: fontFamily,
        fontSizes: fontSizes,
        fontSizeValues: fontSizeValues,
        fontWeights: fontWeights,
        fontWeightValues: fontWeightValues,
        lineHeights: lineHeights,
        lineHeightValues: lineHeightValues,
        sizings: sizings,
        sizingValues: sizingValues,
        spacings: spacings,
        spacingValues: spacingValues,
        textAligns: textAligns,
        zIndexes: zIndexes,
    };
    return {
        hawkins: __assign({ theme: theme }, hawkinsTheme),
        hawkinsVariables: hawkinsVariables,
        hawkinsUtils: hawkinsUtils(hawkinsTheme, hawkinsVariables),
        external: externalTheme,
    };
};
